const PENCIL_ICON =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
const CLOSE_ICON =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';
// "Number" and "Name" in English, German, French, Spanish and Japanese
const NUMBER_WORDS = ["number", "nummer", "numéro", "número", "番号", "ナンバー"];
const NAME_WORDS = ["name", "nom", "nombre", "名", "ネーム"];

class JerseyPersonalization extends HTMLElement {
  connectedCallback() {
    this.section = this.closest(".main-product-section") || document.body;
    this.customVariantIds = (this.dataset.customVariants || "")
      .split(",")
      .filter(Boolean);
    this.triggers = (this.dataset.trigger || "")
      .toLowerCase()
      .split(",")
      .map((word) => word.trim())
      .filter(Boolean);
    this.fields = this.querySelector(".jersey-personalization__fields");
    this.nameInput = this.querySelector("[data-jersey-name]");
    this.numberInput = this.querySelector("[data-jersey-number]");
    if (!this.triggers.length || !this.fields || !this.nameInput || !this.numberInput)
      return;

    this.onInput = this.onInput.bind(this);
    this.onOptionChange = this.onOptionChange.bind(this);
    this.onFormData = this.onFormData.bind(this);

    this.fields.addEventListener("input", this.onInput);
    this.section.addEventListener("change", this.onOptionChange);
    // On document because the sticky form is re-rendered on variant change
    document.addEventListener("formdata", this.onFormData);

    // The theme re-renders the variant picker on every variant change without
    // firing an event, which also drops the fields and the link placed in it
    this.observer = new MutationObserver((mutations) => {
      if (mutations.every((mutation) => this.isOwnMutation(mutation))) return;
      this.render();
    });
    this.observer.observe(this.section, { childList: true, subtree: true });
    this.render();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.fields?.remove();
    this.section?.removeEventListener("change", this.onOptionChange);
    document.removeEventListener("formdata", this.onFormData);
  }

  isOwnMutation(mutation) {
    const target = mutation.target;
    return (
      this.fields.contains(target) ||
      !!target.closest?.("[data-jersey-shortcut]") ||
      !!target.closest?.("legend")
    );
  }

  get mainForm() {
    return document.getElementById(this.dataset.mainFormId);
  }

  isCustomValue(input) {
    const value = (input.dataset.optionValue || "").toLowerCase();
    return this.triggers.some((word) => value.includes(word));
  }

  isCustomVariant(form = this.mainForm) {
    const variantId = form?.querySelector('[name="id"]')?.value;
    return !!variantId && this.customVariantIds.includes(String(variantId));
  }

  findCustomInput() {
    return Array.from(
      this.section.querySelectorAll(
        ".block-product__variant-picker input.variant-options[data-option-value]",
      ),
    ).find((input) => this.isCustomValue(input));
  }

  // "Custom Number" asks for a number only and "Custom Name" for a name
  // only; any other custom value asks for both, in every store language
  fieldsFor(customInput) {
    const value = (customInput?.dataset.optionValue || "").toLowerCase();
    const number = NUMBER_WORDS.some((word) => value.includes(word));
    const name = NAME_WORDS.some((word) => value.includes(word));
    return { number: number || !name, name: name || !number };
  }

  // Puts the picker into or out of custom mode to match the selected value
  render() {
    const customInput = this.findCustomInput();
    const fieldset = customInput?.closest("fieldset");
    const info = fieldset?.querySelector(".product-variants-info");
    if (!info) return;
    const active = customInput.checked;
    this.wanted = this.fieldsFor(customInput);

    if (!active) {
      const selected = fieldset.querySelector("input.variant-options:checked");
      if (selected) this.lastListedValue = selected.value;
    }
    fieldset.querySelectorAll("li.variant-input").forEach((item) => {
      item.classList.toggle(
        "jersey-option-collapsed",
        active || item.contains(customInput),
      );
    });

    if (active && this.fields.previousElementSibling !== info) {
      info.after(this.fields);
    }
    this.fields.hidden = !active;
    this.nameInput.disabled = !active || !this.wanted.name;
    this.numberInput.disabled = !active || !this.wanted.number;
    this.nameInput.closest(".field").hidden = !this.wanted.name;
    this.numberInput.closest(".field").hidden = !this.wanted.number;
    this.fields.classList.toggle(
      "jersey-personalization__fields--single",
      !(this.wanted.name && this.wanted.number),
    );

    this.renderLink(info, active);
    if (active) this.renderLegend(info);
  }

  renderLink(info, active) {
    let link = info.querySelector("[data-jersey-shortcut]");
    if (!link) {
      link = document.createElement("button");
      link.type = "button";
      link.className =
        "button btn-reset btn-link border-animation pointer text-size body_weight";
      link.dataset.jerseyShortcut = "";
      link.addEventListener("click", () => this.toggle());
      info.classList.add("flex", "wrap", "gap-5", "space-between");
      info.appendChild(link);
    }
    const single = !(this.wanted.name && this.wanted.number);
    const openLabel = !single
      ? this.dataset.shortcutLabel
      : this.wanted.number
        ? this.dataset.shortcutLabelNumber
        : this.dataset.shortcutLabelName;
    const label = (active ? this.dataset.backLabel : openLabel) || "";
    const shortLabel =
      (active
        ? this.dataset.backLabelMobile
        : this.dataset.shortcutLabelMobile) || label;
    const key = `${label}|${shortLabel}`;
    if (link.dataset.label === key) return;
    link.dataset.label = key;
    link.innerHTML = active ? CLOSE_ICON : PENCIL_ICON;
    // Both labels are in the link; CSS shows the short one on phones
    [
      ["jersey-shortcut__long", label],
      ["jersey-shortcut__short", shortLabel],
    ].forEach(([className, text]) => {
      const span = document.createElement("span");
      span.className = className;
      span.textContent = text;
      link.append(span);
    });
    link.setAttribute("aria-label", label);
  }

  // Shows the entered number and name next to the option name. Both are
  // optional; the cart sections print the same wording
  renderLegend(info) {
    const value = info.querySelector("legend .option_value");
    if (!value) return;
    const number = this.numberInput.value;
    const name = this.nameInput.value.trim();
    let text = "No name & number";
    if (!this.wanted.name) text = number || "(No number)";
    else if (!this.wanted.number) text = name || "(No name)";
    else if (number && name) text = `${number} - ${name}`;
    else if (number) text = `${number} - (No name)`;
    else if (name) text = `(No number) - ${name}`;
    if (value.textContent !== text) value.textContent = text;
  }

  toggle() {
    const customInput = this.findCustomInput();
    if (!customInput) return;
    if (!customInput.checked) {
      customInput.click();
      (this.wanted.number ? this.numberInput : this.nameInput).focus();
      return;
    }
    const listed = Array.from(
      customInput
        .closest("fieldset")
        .querySelectorAll("input.variant-options[data-option-value]"),
    ).filter((input) => !this.isCustomValue(input));
    const target =
      listed.find((input) => input.value === this.lastListedValue) || listed[0];
    target?.click();
  }

  onOptionChange(event) {
    if (!event.target.matches?.("input.variant-options[data-option-value]"))
      return;
    if (!event.target.closest(".block-product__variant-picker")) return;
    // Switch right away; render() runs again once the variant has loaded
    this.render();
  }

  onInput(event) {
    if (event.target === this.numberInput) {
      this.numberInput.value = this.numberInput.value
        .replace(/\D/g, "")
        .slice(0, 3);
    }
    const info = this.fields.previousElementSibling;
    if (info) this.renderLegend(info);
  }

  // Shopify drops empty properties, so a field left empty is sent as a
  // placeholder to keep it visible on the order. Also covers the sticky bar,
  // whose separate form does not include the fields
  onFormData(event) {
    const form = event.target;
    // form.id would return the variant input named "id", not the attribute
    if (
      form.getAttribute("id") !== this.dataset.mainFormId &&
      form.getAttribute("id") !== this.dataset.stickyFormId
    )
      return;
    if (!this.isCustomVariant(form)) return;
    const wanted = this.fieldsFor(this.findCustomInput());
    const fields = [
      [wanted.number, this.numberInput, this.numberInput.value, "(No number)"],
      [wanted.name, this.nameInput, this.nameInput.value.trim(), "(No name)"],
    ];
    for (const [isWanted, input, value, placeholder] of fields) {
      if (isWanted) event.formData.set(input.name, value || placeholder);
      else event.formData.delete(input.name);
    }
  }
}

if (!customElements.get("jersey-personalization")) {
  customElements.define("jersey-personalization", JerseyPersonalization);
}
